// src/pages_unauth/SignupPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SignupPage.css";
import "./SignupPageMobile.css";
import { FcGoogle } from "react-icons/fc";
import { LuBrain, LuHeadphones } from "react-icons/lu";
import { FiBookOpen } from "react-icons/fi";
import { FaApple } from "react-icons/fa";
import { ReactComponent as AprendoleIcon } from "../icons/AprendoleIcon5.svg";

import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";

import { useAuth } from "../context/AuthContext";
import { upsertUserProfileOnSignup, upsertUserProfileAfterOAuth } from "../data/accountRepo";
import { Capacitor } from "@capacitor/core";

/**
 * Keep these in ONE place and bump when docs change materially.
 */
const TERMS_VERSION = "2025-12-22";
const PRIVACY_VERSION = "2025-12-22";
const COOKIE_VERSION = "2025-12-22";
const MARKETING_CONSENT_VERSION = "2025-12-22";

const CONSENT_SESSION_KEY = "aprendo_signup_consent_v1";

const isNative = Capacitor.isNativePlatform();

const SignupPage = () => {
  const {
    user,
    signUpEmailPassword,
    sendVerificationEmail,
    signInWithGooglePopup,
    signInWithApple,
  } = useAuth();

  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [consent, setConsent] = useState({
    acceptTermsPrivacy: isNative ? true : false,
    marketingOptIn: false,
  });

  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setConsent((prev) => ({ ...prev, [name]: checked }));
  };

  const buildConsentPayload = () => ({
    acceptTermsPrivacy: consent.acceptTermsPrivacy,
    marketingOptIn: consent.marketingOptIn,
    versions: {
      terms: TERMS_VERSION,
      privacy: PRIVACY_VERSION,
      cookie: COOKIE_VERSION,
      marketing: MARKETING_CONSENT_VERSION,
    },
  });

  /**
   * ✅ After OAuth redirect completes, AuthContext finalizes it globally.
   * When `user` becomes available, we check if we have stored consent from before redirect.
   * If yes, we write it via repo and then clear the session key.
   * Was done fore GoogleSigninwithRedirect, now we use pop up not neccessary anymore
   */
  // useEffect(() => {
  //   if (!user) return;

  //   const stored = sessionStorage.getItem(CONSENT_SESSION_KEY);
  //   console.log("[SignupPage] user became available", {
  //     uid: user.uid,
  //     email: user.email,
  //     displayName: user.displayName,
  //     hasConsentKey: !!sessionStorage.getItem(CONSENT_SESSION_KEY),
  //   });
    
  //   if (!stored) return;

  //   let parsed = null;
  //   try {
  //     parsed = JSON.parse(stored);
  //   } catch {
  //     sessionStorage.removeItem(CONSENT_SESSION_KEY);
  //     return;
  //   }

  //   // Only run if consent was actually accepted
  //   if (!parsed?.acceptTermsPrivacy) {
  //     sessionStorage.removeItem(CONSENT_SESSION_KEY);
  //     return;
  //   }

  //   // Prevent double-run in strict mode / rerenders
  //   sessionStorage.removeItem(CONSENT_SESSION_KEY);

  //   (async () => {
  //     try {
  //       await upsertUserProfileAfterOAuth({
  //         uid: user.uid,
  //         name: user.displayName || "",  // Google display name
  //         email: user.email || "",
  //         consent: {
  //           acceptTermsPrivacy: !!parsed.acceptTermsPrivacy,
  //           marketingOptIn: !!parsed.marketingOptIn,
  //         },
  //         versions: parsed.versions,
  //       });

  //       navigate("/home", { replace: true });
  //     } catch (err) {
  //       console.error("Error writing OAuth signup consent:", err);
  //       setError("Erro ao finalizar cadastro com Google. Tente novamente.");
  //     }
  //   })();
  // }, [user, navigate]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (isOffline) return;

    if (!consent.acceptTermsPrivacy) {
      setError("Para criar uma conta, você precisa aceitar os Termos e a Política de Privacidade.");
      return;
    }

    try {
      // 1) Create Auth account
      const createdUser = await signUpEmailPassword(form.email, form.password);

      // 2) Send email verification
      await sendVerificationEmail(createdUser);

      // 3) Save Firestore profile + consents
      await upsertUserProfileOnSignup({
        uid: createdUser.uid,
        name: form.name,
        email: form.email,
        consent: {
          acceptTermsPrivacy: consent.acceptTermsPrivacy,
          marketingOptIn: consent.marketingOptIn,
        },
        versions: {
          terms: TERMS_VERSION,
          privacy: PRIVACY_VERSION,
          cookie: COOKIE_VERSION,
          marketing: MARKETING_CONSENT_VERSION,
        },
      });

      if (analytics) {
        logEvent(analytics, "sign_up", { method: "email" });
      }

      navigate("/verify-email");
    } catch (err) {
      setError(err?.message || "Erro ao criar conta.");
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");

    if (isOffline) return;
  
    if (!consent.acceptTermsPrivacy) {
      setError("Para continuar com Google, você precisa aceitar os Termos e a Política de Privacidade.");
      return;
    }
  
    try {
      const oauthUser = await signInWithGooglePopup();
  
      await upsertUserProfileAfterOAuth({
        uid: oauthUser.uid,
        name: oauthUser.displayName || form.name || "",
        email: oauthUser.email || "",
        consent: {
          acceptTermsPrivacy: consent.acceptTermsPrivacy,
          marketingOptIn: consent.marketingOptIn,
        },
        versions: {
          terms: TERMS_VERSION,
          privacy: PRIVACY_VERSION,
          cookie: COOKIE_VERSION,
          marketing: MARKETING_CONSENT_VERSION,
        },
      });
      
      if (analytics) {
        logEvent(analytics, "sign_up", { method: "google" });
      }
      
  
      navigate("/home", { replace: true });
    } catch (err) {
      console.error("Google sign-up error", err);
      setError("Erro ao continuar com Google. Tente novamente.");
    }
  };

  const handleAppleSignIn = async () => {
    setError("");

    if (isOffline) return;
  
    if (!consent.acceptTermsPrivacy) {
      setError("Para continuar com Apple, você precisa aceitar os Termos e a Política de Privacidade.");
      return;
    }
  
    try {
      const oauthUser = await signInWithApple();
  
      await upsertUserProfileAfterOAuth({
        uid: oauthUser.uid,
        name: oauthUser.displayName || form.name || "",
        email: oauthUser.email || "",
        consent: {
          acceptTermsPrivacy: consent.acceptTermsPrivacy,
          marketingOptIn: consent.marketingOptIn,
        },
        versions: {
          terms: TERMS_VERSION,
          privacy: PRIVACY_VERSION,
          cookie: COOKIE_VERSION,
          marketing: MARKETING_CONSENT_VERSION,
        },
      });
  
      navigate("/home", { replace: true });

      if (analytics) {
        logEvent(analytics, "sign_up", { method: "apple" });
      }
      
    } catch (err) {
      console.error("Apple sign-up error", err);
      setError("Erro ao continuar com Apple. Tente novamente.");
    }
  };

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // useEffect(() => {
  //   const app = document.querySelector('.App');
  //   const container = document.querySelector('.mobile-signup-container');
  //   console.log('window innerHeight:', window.innerHeight);
  //   console.log('body scrollHeight:', document.body.scrollHeight);
  //   console.log('body clientHeight:', document.body.clientHeight);
  //   console.log('App scrollHeight:', app?.scrollHeight, 'clientHeight:', app?.clientHeight, 'offsetHeight:', app?.offsetHeight);
  //   console.log('Container scrollHeight:', container?.scrollHeight, 'clientHeight:', container?.clientHeight, 'offsetHeight:', container?.offsetHeight);
  //   console.log('safe-area-inset-bottom env:', getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom'));
  // }, []);


  // ─── Mobile layout ────────────────────────────────────────────────────────

  if (isNative) {
    return (
      <div className="mobile-signup-page">
      <div className="mobile-signup-container">
        <div className="mobile-signup-top">
          <AprendoleIcon className="mobile-signup-logo" />
          <h1 className="mobile-signup-title">Bem-vindo</h1>
          <p className="mobile-signup-subtitle">Aprenda um idioma com conteúdo real.</p>
        </div>

        {isOffline && (
            <div className="offline-banner">
              <span>Sem internet. Verifique sua conexão</span>
            </div>
          )}

        <form className="mobile-signup-form" onSubmit={handleSignup}>
          <input
            type="email"
            name="email"
            placeholder="E-mail"
            className="mobile-signup-input"
            value={form.email}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Senha"
            className="mobile-signup-input"
            value={form.password}
            onChange={handleChange}
            required
          />

          {error && <p className="mobile-signup-error">{error}</p>}

          <button type="submit" className="mobile-signup-btn-primary">
            Criar Conta
          </button>
        </form>

        <div className="mobile-signup-divider">
          <span className="mobile-signup-divider-line" />
          <span className="mobile-signup-divider-text">ou</span>
          <span className="mobile-signup-divider-line" />
        </div>

        <div className="mobile-signup-social">
          <button
            className="mobile-signup-btn-apple"
            onClick={handleAppleSignIn}
            type="button"
          >
            <FaApple className="mobile-signup-social-icon" />
            <span>Continuar com Apple</span>
          </button>

          <button
            className="mobile-signup-btn-google"
            onClick={handleGoogleSignIn}
            type="button"
          >
            <FcGoogle className="mobile-signup-social-icon" />
            <span>Continuar com Google</span>
          </button>

          <p className="mobile-signup-account">
            Já tem uma conta?{" "}
            <a href="/signin" className="mobile-signup-link">Entrar</a>
          </p>
        </div>

        <div className="mobile-signup-footer">
          <p className="mobile-signup-legal">
            Ao continuar, você concorda com os nossos{" "}
            <a href="/terms-and-conditions" className="mobile-signup-link">Termos</a>
            {" "}e{" "}
            <a href="/privacy-policy" className="mobile-signup-link">Política de Privacidade</a>
          </p>
        </div>
      </div>
      </div>
    );
  }


// Web Layout

  return (
    <div className="sign-container">
      <div className="sign-content">
        <div className="sign-left">
          <header className="sign-hero">
            <h1 className="sign-hero-title">
              Comece a <span className="shiny-text">Aprender</span> Hoje
            </h1>
            <p className="sign-hero-subtitle">Comece grátis e sem cartão de crédito.</p>
          </header>

          <div className="sign-card">
            <form className="sign-form" onSubmit={handleSignup}>
              <input
                type="text"
                name="name"
                placeholder="Nome"
                className="sign-input"
                value={form.name}
                onChange={handleChange}
                required
              />

              <input
                type="email"
                name="email"
                placeholder="E-mail"
                className="sign-input"
                value={form.email}
                onChange={handleChange}
                required
              />

              <input
                type="password"
                name="password"
                placeholder="Criar senha"
                className="sign-input"
                value={form.password}
                onChange={handleChange}
                required
              />

              <div className="sign-consents">
                <label className="sign-checkbox">
                  <input
                    type="checkbox"
                    name="acceptTermsPrivacy"
                    checked={consent.acceptTermsPrivacy}
                    onChange={handleConsentChange}
                  />
                  <span>
                    Eu aceito os{" "}
                    <a href="/terms-and-conditions" className="sign-link">
                      Termos e Condições
                    </a>{" "}
                    e a{" "}
                    <a href="/privacy-policy" className="sign-link">
                      Política de Privacidade
                    </a>
                    .
                  </span>
                </label>

                <label className="sign-checkbox">
                  <input
                    type="checkbox"
                    name="marketingOptIn"
                    checked={consent.marketingOptIn}
                    onChange={handleConsentChange}
                  />
                  <span>Quero receber novidades e lançamentos.</span>
                </label>
              </div>

              {error && <p className="error">{error}</p>}

              <button type="submit" className="sign-button">
                Criar Conta
              </button>
            </form>

            <div className="sign-divider">
              <span className="sign-line"></span>
              <span className="sign-or">OU</span>
              <span className="sign-line"></span>
            </div>

            {isNative && (
            <div className="sign-google-container">
            <button
                className="apple-signin-btn"
                onClick={handleAppleSignIn}
                aria-label="Continuar com Apple"
              >
                <FaApple className="sign-provider-icon" />
                <span className="sign-provider-text">Continuar com Apple</span>
              </button>
            </div>
            )}

            <div className="sign-google-container">
            <button
              className="sign-google-button"
              onClick={handleGoogleSignIn}
              type="button"
            >
              <FcGoogle className="sign-provider-icon" />
              <span className="sign-provider-text">Continuar com Google</span>
            </button>
            </div>

            <p className="sign-account-text">
              Já tem uma conta?{" "}
              <a href="/signin" className="sign-link">
                Entrar
              </a>
            </p>
          </div>
        </div>

        <div className="sign-right">
          <div className="sign-feature">
            <div className="sign-feature-icon">
              <FiBookOpen />
            </div>
            <div>
              <h3 className="sign-feature-title">Aprenda com histórias</h3>
              <p className="sign-feature-text">
                Aprende enquanto consome histórias e ideias que realmente interessam.
              </p>
            </div>
          </div>

          <div className="sign-feature">
            <div className="sign-feature-icon">
              <LuHeadphones />
            </div>
            <div>
              <h3 className="sign-feature-title">Treine seu ouvido</h3>
              <p className="sign-feature-text">
                Áudio sincronizado em cada história para melhorar a compreensão.
              </p>
            </div>
          </div>

          <div className="sign-feature">
            <div className="sign-feature-icon">
              <LuBrain />
            </div>
            <div>
              <h3 className="sign-feature-title">Expanda seu vocabulário</h3>
              <p className="sign-feature-text">
                Salve palavras importantes que você ainda não conhece e revise no seu ritmo com
                repetição espaçada inteligente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;



